import { useMemo, useState } from 'react';

import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';

import { useHSOverlay } from '@core/hooks/useHSOverlay';

/**
 * Modal for selecting a destination folder when moving a document.
 *
 * Shows a tree of folders with radio-style selection. Includes a
 * "Project root" option for moving to the top level.
 */
export function FolderPickerModal({
  isOpen,
  onConfirm,
  onCancel,
  folders = [],
  currentFolderUuid,
}) {
  const { t } = useTranslation();
  const { overlayId, overlayRef } = useHSOverlay(isOpen, onCancel, 'hs-folder-picker');
  const [selectedFolderUuid, setSelectedFolderUuid] = useState(null);

  const rootFolders = useMemo(() => folders.filter((f) => !f.parent_uuid), [folders]);
  const childrenOf = useMemo(() => {
    const map = new Map();
    for (const f of folders) {
      const key = f.parent_uuid || '__root__';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    return (parentUuid) => map.get(parentUuid) || [];
  }, [folders]);

  const handleConfirm = () => {
    onConfirm(selectedFolderUuid);
    setSelectedFolderUuid(null);
  };

  const handleCancel = () => {
    setSelectedFolderUuid(null);
    onCancel();
  };

  const isRootSelected = selectedFolderUuid === null && currentFolderUuid !== null;
  const isCurrentRoot = currentFolderUuid === null || currentFolderUuid === undefined;

  return (
    <div
      id={overlayId}
      ref={overlayRef}
      className="hs-overlay hidden size-full fixed top-0 start-0 z-80 overflow-x-hidden overflow-y-auto pointer-events-none"
      role="dialog"
      tabIndex={-1}
      aria-labelledby={`${overlayId}-label`}
    >
      <div className="hs-overlay-open:mt-7 hs-overlay-open:opacity-100 hs-overlay-open:duration-500 mt-0 opacity-0 ease-out transition-all sm:max-w-md sm:w-full m-3 sm:mx-auto min-h-[calc(100%-56px)] flex items-center">
        <div className="w-full flex flex-col bg-white border border-[var(--color-border)] shadow-sm rounded-lg pointer-events-auto">
          <div className="p-4 sm:p-6">
            <h3 id={`${overlayId}-label`} className="text-lg font-semibold text-[var(--color-text)] mb-4">
              {t('dataroom.folderPicker.title')}
            </h3>

            <div className="max-h-64 overflow-y-auto border border-[var(--color-border)] rounded-lg">
              {/* Root option */}
              <button
                type="button"
                onClick={() => setSelectedFolderUuid(null)}
                disabled={isCurrentRoot}
                className={`cursor-pointer w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                  isRootSelected
                    ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]'
                    : isCurrentRoot
                      ? 'opacity-50 cursor-not-allowed bg-[var(--color-bg-muted)]'
                      : 'hover:bg-[var(--color-bg-muted)]'
                }`}
              >
                <Icon icon="mdi:home-outline" className="size-4 shrink-0" />
                <span>{t('dataroom.folderPicker.projectRoot')}</span>
                {isCurrentRoot && (
                  <span className="text-xs text-[var(--color-text-muted)] ml-auto">{t('dataroom.folderPicker.current')}</span>
                )}
              </button>

              {/* Folder tree */}
              {rootFolders.map((folder) => (
                <FolderPickerNode
                  key={folder.uuid}
                  folder={folder}
                  depth={0}
                  childrenOf={childrenOf}
                  selectedFolderUuid={selectedFolderUuid}
                  currentFolderUuid={currentFolderUuid}
                  onSelect={setSelectedFolderUuid}
                />
              ))}
            </div>
          </div>

          <div className="flex justify-end items-center gap-x-2 py-3 px-4 border-t border-[var(--color-border)]">
            <button
              type="button"
              onClick={handleCancel}
              className="cursor-pointer py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-[var(--color-border)] bg-white text-[var(--color-text)] shadow-sm hover:bg-[var(--color-bg-muted)] focus:outline-hidden"
            >
              {t('dataroom.folderPicker.cancel')}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={selectedFolderUuid === currentFolderUuid}
              className="cursor-pointer py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] focus:outline-hidden disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('dataroom.folderPicker.moveHere')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FolderPickerNode({ folder, depth, childrenOf, selectedFolderUuid, currentFolderUuid, onSelect }) {
  const { t } = useTranslation();
  const children = childrenOf(folder.uuid);
  const isSelected = selectedFolderUuid === folder.uuid;
  const isCurrent = currentFolderUuid === folder.uuid;

  return (
    <>
      <button
        type="button"
        onClick={() => onSelect(folder.uuid)}
        disabled={isCurrent}
        className={`cursor-pointer w-full flex items-center gap-2 py-2 text-sm text-left transition-colors ${
          isSelected
            ? 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]'
            : isCurrent
              ? 'opacity-50 cursor-not-allowed bg-[var(--color-bg-muted)]'
              : 'hover:bg-[var(--color-bg-muted)]'
        }`}
        style={{ paddingLeft: `${depth * 16 + 12}px` }}
      >
        <Icon icon="mdi:folder" className="size-4 text-yellow-500 shrink-0" />
        <span className="truncate">{folder.name}</span>
        {isCurrent && (
          <span className="text-xs text-[var(--color-text-muted)] ml-auto pr-3">{t('dataroom.folderPicker.current')}</span>
        )}
      </button>
      {children.map((child) => (
        <FolderPickerNode
          key={child.uuid}
          folder={child}
          depth={depth + 1}
          childrenOf={childrenOf}
          selectedFolderUuid={selectedFolderUuid}
          currentFolderUuid={currentFolderUuid}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}
